import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { gaEvents, getGaClientId } from '@/hooks/useGoogleAnalytics';
import { forwardLead } from '@/lib/forwardLead';

// Sanitize input to prevent XSS and SQL injection
const sanitizeInput = (input: string, maxLength: number = 500): string => {
  return input
    .trim()
    .slice(0, maxLength)
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, ''); // Remove event handlers
};

// Validate phone number format
const isValidPhone = (phone: string): boolean => {
  if (!phone) return true; // Phone is optional
  const phoneRegex = /^[\d\s\-\+\(\)\.]{7,30}$/;
  return phoneRegex.test(phone);
};

interface LeadCaptureFormProps {
  siteId?: string;
  townId?: string | null;
  areaId?: string | null;
  pagePath?: string;
  className?: string;
  variant?: 'default' | 'hero';
  siteDomain?: string;
  townName?: string;
  areaName?: string;
}

export function LeadCaptureForm({
  siteId,
  townId = null,
  areaId = null,
  pagePath = '',
  className = '',
  variant = 'default',
  siteDomain = '',
  townName = '',
  areaName = '',
}: LeadCaptureFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState(''); // Spam protection
  const [formStarted, setFormStarted] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const categoryOptions = ['Buyer', 'Seller', 'Broker', 'Mortgage Officer', 'Other'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!siteId) {
      toast({
        title: 'Error',
        description: 'Site not configured. Please try again later.',
        variant: 'destructive',
      });
      return;
    }

    // Honeypot check - if filled, it's likely a bot
    if (honeypot) {
      // Silently reject but show success to not alert bots
      toast({
        title: 'Thank you!',
        description: 'We\'ve received your message and will be in touch soon.',
      });
      return;
    }

    // Sanitize inputs
    const sanitizedName = sanitizeInput(name, 100);
    const sanitizedEmail = email.trim().toLowerCase().slice(0, 255);
    const sanitizedPhone = sanitizeInput(phone, 30);
    const sanitizedMessage = sanitizeInput(message, 1000);

    if (!sanitizedName || !sanitizedEmail) {
      toast({
        title: 'Required fields',
        description: 'Please fill in your name and email.',
        variant: 'destructive',
      });
      return;
    }

    // Category validation
    if (category.length === 0) {
      toast({
        title: 'Category required',
        description: 'Please select at least one category (I am a...).',
        variant: 'destructive',
      });
      return;
    }

    // Consent validation
    if (!consentGiven) {
      toast({
        title: 'Consent required',
        description: 'Please agree to receive communications before submitting.',
        variant: 'destructive',
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedEmail)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    // Phone validation
    if (sanitizedPhone && !isValidPhone(sanitizedPhone)) {
      toast({
        title: 'Invalid phone',
        description: 'Please enter a valid phone number.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Get GA client ID for retargeting
      const gaClientId = getGaClientId();

      const { error } = await supabase.from('leads').insert({
        site_id: siteId,
        town_id: townId || null,
        area_id: areaId || null,
        name: sanitizedName,
        email: sanitizedEmail,
        phone: sanitizedPhone || null,
        message: sanitizedMessage || null,
        page_path: pagePath?.slice(0, 255) || null,
        ga_client_id: gaClientId,
        site_domain: siteDomain || null,
        town_name: townName || null,
        area_name: areaName || null,
        category: category,
      });

      if (error) throw error;

      // Forward lead to external API (non-blocking)
      forwardLead({
        name: sanitizedName,
        email: sanitizedEmail,
        phone: sanitizedPhone || null,
        message: sanitizedMessage || null,
        site_id: siteId,
        town_id: townId || null,
        area_id: areaId || null,
        page_path: pagePath || null,
        request_type: 'contact',
        site_domain: siteDomain || null,
        town_name: townName || null,
        area_name: areaName || null,
        category: category,
      });

      // Track lead generation event
      gaEvents.generateLead('contact', {
        page_path: pagePath,
        has_phone: !!sanitizedPhone,
        has_message: !!sanitizedMessage
      });

      // Show thank you state
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const isHero = variant === 'hero';

  if (submitted) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="mb-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <h4 className="text-xl font-semibold mb-2">Thank You!</h4>
        <p className="text-muted-foreground">
          We've received your message and will be in touch soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      {/* Honeypot field - hidden from users, visible to bots */}
      <div className="hidden" aria-hidden="true">
        <Input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="name" className={isHero ? 'text-primary-foreground' : ''}>
          Name *
        </Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!formStarted) {
              setFormStarted(true);
              gaEvents.formStart('contact', pagePath);
            }
          }}
          placeholder="Your name"
          required
          maxLength={100}
          className={isHero ? 'bg-background/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50' : ''}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email" className={isHero ? 'text-primary-foreground' : ''}>
          Email *
        </Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          maxLength={255}
          className={isHero ? 'bg-background/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50' : ''}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone" className={isHero ? 'text-primary-foreground' : ''}>
          Phone
        </Label>
        <Input
          id="phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(555) 123-4567"
          maxLength={30}
          className={isHero ? 'bg-background/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50' : ''}
        />
      </div>
      <div className="space-y-2">
        <Label className={isHero ? 'text-primary-foreground' : ''}>
          I am a... *
        </Label>
        <div className={`flex flex-wrap gap-2 ${isHero ? '' : ''}`}>
          {categoryOptions.map((option) => (
            <label
              key={option}
              className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                category.includes(option)
                  ? isHero
                    ? 'bg-primary-foreground text-primary border-primary-foreground'
                    : 'bg-primary text-primary-foreground border-primary'
                  : isHero
                    ? 'bg-background/10 border-primary-foreground/30 text-primary-foreground hover:bg-background/20'
                    : 'bg-background border-input hover:bg-muted'
              }`}
            >
              <Checkbox
                checked={category.includes(option)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setCategory([...category, option]);
                  } else {
                    setCategory(category.filter((c) => c !== option));
                  }
                }}
                className="sr-only"
              />
              <span className="text-sm">{option}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="message" className={isHero ? 'text-primary-foreground' : ''}>
          Message
        </Label>
        <Textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tell us about your real estate needs..."
          rows={4}
          className={isHero ? 'bg-background/10 border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50' : ''}
        />
      </div>
      <div className="flex items-start space-x-3">
        <Checkbox
          id="consent"
          checked={consentGiven}
          onCheckedChange={(checked) => setConsentGiven(checked === true)}
          className={isHero ? 'border-primary-foreground/50 data-[state=checked]:bg-primary-foreground data-[state=checked]:text-primary' : ''}
        />
        <label
          htmlFor="consent"
          className={`text-xs leading-relaxed cursor-pointer ${isHero ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}
        >
          By submitting this form, you consent to be contacted by email, phone, and text, and to the use of your information for advertising and personalized advertising. Message and data rates may apply. Reply STOP to opt out.
        </label>
      </div>
      <Button
        type="submit"
        variant={isHero ? 'gold' : 'accent'}
        size="lg"
        className="w-full"
        disabled={loading}
      >
        {loading ? 'Sending...' : 'Buying or Selling?'}
      </Button>
    </form>
  );
}
